import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './App.css';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Auth Components
import ProtectedRoute from './components/auth/ProtectedRoute';

// Auth Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// App Pages
import HomePage from './pages/HomePage';
import TeamsPage from './pages/TeamsPage';
import TeamDashboardPage from './pages/TeamDashboardPage';
import UploadPage from './pages/UploadPage';
import MatchListPage from './pages/MatchListPage';
import ReviewPage from './pages/ReviewPage';
import StatsPage from './pages/StatsPage';
import PlayerInsightsPage from './pages/PlayerInsightsPage';

// Navigation component with auth awareness
function Navigation() {
  const { user, logout, currentTeam } = useAuth();

  return (
    <nav className="navbar">
      <div className="nav-container">
        <Link to="/" className="nav-brand">
          🏐 VB-IQ
        </Link>
        <div className="nav-links">
          <Link to="/" className="nav-link">Home</Link>
          {user ? (
            <>
              <Link to="/teams" className="nav-link">Teams</Link>
              {currentTeam && <span className="nav-team">{currentTeam.name}</span>}
              <Link to="/upload" className="nav-link">Upload</Link>
              <Link to="/matches" className="nav-link">Matches</Link>
              <button onClick={logout} className="nav-link nav-logout">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="nav-link">Login</Link>
              <Link to="/register" className="nav-link nav-cta">Sign Up</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

function AppContent() {
  return (
    <div className="App">
      <Navigation />

      <main className="main-content">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected routes - require authentication */}
          <Route element={<ProtectedRoute />}>
            <Route path="/teams" element={<TeamsPage />} />
            <Route path="/teams/:teamId" element={<TeamDashboardPage />} />
            <Route path="/upload" element={<UploadPage />} />
            <Route path="/matches" element={<MatchListPage />} />
            <Route path="/matches/:matchId/review" element={<ReviewPage />} />
            <Route path="/matches/:matchId/stats" element={<StatsPage />} />
            <Route path="/players/:playerId/insights" element={<PlayerInsightsPage />} />
          </Route>
        </Routes>
      </main>

      <footer className="footer">
        <p>VB-IQ - Track, Analyze, Improve</p>
      </footer>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#333',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#00C851',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ff4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
